import React, { useMemo, useState } from "react"

import { useAgentsList } from "@/agents/catalog"
import { SearchInput } from "@coval/ui"
import { Check } from "lucide-react"

interface AgentAttachButtonProps {
  selectedAgentIds: string[]
  onAgentToggled: (agentId: string) => void
}

export const AgentAttachButton: React.FC<AgentAttachButtonProps> = ({
  selectedAgentIds,
  onAgentToggled,
}) => {
  const [searchQuery, setSearchQuery] = useState("")

  const { data: agents, isLoading: isLoadingAgents } = useAgentsList()

  const filteredAgents = useMemo(() => {
    if (!searchQuery) {
      return agents || []
    }
    return (agents || []).filter(
      (agent) =>
        agent.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.id.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [agents, searchQuery])

  return (
    <div className="flex flex-col w-full">
      <div className="p-2">
        <SearchInput
          aria-label="Search agents"
          placeholder="Search agents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </div>
      <div
        className="max-h-[300px] overflow-y-auto"
        style={{ scrollbarWidth: "thin" }}
      >
        {filteredAgents.length === 0 ? (
          <div className="px-3 py-2 text-sm text-coval-text-muted">
            {isLoadingAgents
              ? "Loading..."
              : searchQuery
                ? "No agents found"
                : "No agents available"}
          </div>
        ) : (
          filteredAgents.map((agent) => {
            const isSelected = selectedAgentIds.includes(agent.id)
            return (
              <button
                type="button"
                key={agent.id}
                className="flex items-center justify-between w-full px-3 py-2 hover:bg-surface-1 text-left"
                onClick={() => onAgentToggled(agent.id)}
                aria-pressed={isSelected}
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {agent.display_name}
                  </span>
                  <span className="text-xs text-coval-text-muted">
                    {agent.id}
                  </span>
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 text-coval-contrast flex-shrink-0" />
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
